"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Clock, ArrowUpDown, Search, ChevronUp, ChevronDown } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Input } from "@/components/ui/input"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import LeaveManagement from "./LeaveManagement"
import { API_ENDPOINTS } from "../../../constants/config"
import { toast } from "react-hot-toast"
import { getFullName, getInitial, getTeacherProfileUrl } from "../../../utils/urlUtils"
import Pagination from "../../Common/Pagination"

const TeacherAttendance = () => {
  const navigate = useNavigate()
  const getTodayDate = () => {
    const today = new Date()
    const offset = today.getTimezoneOffset()
    const localDate = new Date(today.getTime() - offset * 60 * 1000)
    return localDate.toISOString().split("T")[0]
  }

  const [sortColumn, setSortColumn] = useState("name")
  const [sortDirection, setSortDirection] = useState("asc")
  const [teachers, setTeachers] = useState([])
  const [filteredTeachers, setFilteredTeachers] = useState([])
  const [selectedDate, setSelectedDate] = useState(getTodayDate())
  const [loading, setLoading] = useState(false)
  const [attendanceData, setAttendanceData] = useState([])
  const [message, setMessage] = useState({ type: "", content: "" })
  const [fetchError, setFetchError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Staff tab state
  const [staff, setStaff] = useState([])
  const [filteredStaff, setFilteredStaff] = useState([])
  const [staffAttendanceData, setStaffAttendanceData] = useState([])
  const [staffSearchQuery, setStaffSearchQuery] = useState("")
  const [staffCurrentPage, setStaffCurrentPage] = useState(1)
  const [staffSelectedDate, setStaffSelectedDate] = useState(getTodayDate())

  useEffect(() => {
    const initializeData = async () => {
      await fetchTeachers()
      await fetchStaff()
    }
    initializeData()
  }, [])

  useEffect(() => {
    if (teachers.length > 0) {
      fetchAttendance()
    }
  }, [teachers, selectedDate])

  useEffect(() => {
    if (staff.length > 0) {
      fetchStaffAttendance()
    }
  }, [staff, staffSelectedDate])

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredTeachers(teachers)
    } else {
      const filtered = teachers.filter(
        (teacher) =>
          getFullName(teacher).toLowerCase().includes(searchQuery.toLowerCase()) ||
          (teacher.employeeCode && teacher.employeeCode.toLowerCase().includes(searchQuery.toLowerCase())),
      )
      setFilteredTeachers(filtered)
    }
  }, [searchQuery, teachers])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery])

  useEffect(() => {
    if (staffSearchQuery.trim() === "") {
      setFilteredStaff(staff)
    } else {
      const filtered = staff.filter(
        (s) =>
          getFullName(s).toLowerCase().includes(staffSearchQuery.toLowerCase()) ||
          (s.staffId && s.staffId.toLowerCase().includes(staffSearchQuery.toLowerCase())) ||
          (s.employeeCode && s.employeeCode.toLowerCase().includes(staffSearchQuery.toLowerCase())),
      )
      setFilteredStaff(filtered)
    }
  }, [staffSearchQuery, staff])

  useEffect(() => {
    setStaffCurrentPage(1)
  }, [staffSearchQuery])

  const fetchStaff = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("No authentication token found")
      const response = await fetch(API_ENDPOINTS.usersStaff, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      })
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      const data = await response.json()
      if (data.status === "success") {
        const sorted = sortTeacherData(data.data.staff || [], sortColumn, sortDirection)
        setStaff(sorted)
        setFilteredStaff(sorted)
      } else {
        toast.error("Failed to fetch staff: " + (data.message || ""))
      }
    } catch (error) {
      toast.error("Error fetching staff: " + error.message)
      console.error("Staff fetch error:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchStaffAttendance = async () => {
    try {
      setLoading(true)
      const response = await fetch(API_ENDPOINTS.staffAttendanceByDate(staffSelectedDate), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      })
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      const data = await response.json()
      if (data.status === "success" && Array.isArray(data.data.attendance)) {
        const initialAttendance = staff.map((s) => {
          const existingRecord = data.data.attendance.find(
            (r) => r.teacherId?.toString() === s._id.toString() || r.staffId?.toString() === s._id.toString(),
          )
          return {
            staffId: s._id,
            status: existingRecord?.status === "not-marked" ? "unassigned" : (existingRecord?.status || "unassigned"),
            remarks: existingRecord?.remarks || "",
            clockInTime: existingRecord?.clockInTime ?? null,
            clockOutTime: existingRecord?.clockOutTime ?? null,
          }
        })
        setStaffAttendanceData(initialAttendance)
      } else {
        setStaffAttendanceData(
          staff.map((s) => ({
            staffId: s._id,
            status: "unassigned",
            remarks: "",
            clockInTime: null,
            clockOutTime: null,
          })),
        )
      }
    } catch (error) {
      toast.error("Error fetching staff attendance: " + error.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchTeachers = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("No authentication token found")
      }

      const response = await fetch(API_ENDPOINTS.usersTeachers, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.status === "success") {
        const sortedTeachers = sortTeacherData(data.data.teachers, sortColumn, sortDirection)
        setTeachers(sortedTeachers)
        setFilteredTeachers(sortedTeachers)
      } else {
        setFetchError("Failed to fetch teachers: " + data.message)
        toast.error("Failed to fetch teachers: " + data.message)
      }
    } catch (error) {
      setFetchError("Error fetching teachers: " + error.message)
      toast.error("Error fetching teachers: " + error.message)
      console.error("Teacher fetch error:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAttendance = async () => {
    try {
      setLoading(true)
      const response = await fetch(API_ENDPOINTS.teacherAttendanceByDate(selectedDate), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.status === "success" && Array.isArray(data.data.attendance)) {
        const initialAttendance = teachers.map((teacher) => {
          const existingRecord = data.data.attendance.find((record) => {
            return record.teacherId.toString() === teacher._id.toString()
          })

          return {
            teacherId: teacher._id,
            status: existingRecord?.status === "not-marked" ? "unassigned" : (existingRecord?.status || "unassigned"),
            remarks: existingRecord?.remarks || "",
            clockInTime: existingRecord?.clockInTime ?? null,
            clockOutTime: existingRecord?.clockOutTime ?? null,
          }
        })

        setAttendanceData(initialAttendance)
      } else {
        setAttendanceData(
          teachers.map((teacher) => ({
            teacherId: teacher._id,
            status: "unassigned",
            remarks: "",
            clockInTime: null,
            clockOutTime: null,
          })),
        )
      }
    } catch (error) {
      setMessage({
        type: "error",
        content: "Error fetching attendance: " + error.message,
      })
      toast.error("Error fetching attendance: " + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleAttendanceChange = (teacherId, status) => {
    const teacher = teachers.find((t) => t._id === teacherId)
    const teacherName = teacher ? getFullName(teacher) : "Teacher"

    setAttendanceData((prev) => {
      const updated = prev.map((record) => (record.teacherId === teacherId ? { ...record, status } : record))
      return updated
    })

    // Show toast when status changes
    if (status === "present") {
      toast.success(`${teacherName} marked as present`)
    } else if (status === "absent") {
      toast.error(`${teacherName} marked as absent`)
    } else if (status === "half-day") {
      toast.custom(
        <div className="bg-yellow-100 text-yellow-800 px-4 py-3 rounded-lg shadow">
          {teacherName} marked as half-day
        </div>,
      )
    } else if (status === "leave") {
      toast.custom(
        <div className="bg-blue-100 text-blue-800 px-4 py-3 rounded-lg shadow">{teacherName} marked on leave</div>,
      )
    } else if (status === "unassigned") {
      toast.custom(
        <div className="bg-gray-100 text-gray-700 px-4 py-3 rounded-lg shadow">{teacherName} set to Unassigned</div>,
      )
    }
  }

  const handleRemarkChange = (teacherId, remarks) => {
    setAttendanceData((prev) => {
      return prev.map((record) => (record.teacherId === teacherId ? { ...record, remarks } : record))
    })
  }

  const handleEntryChange = (teacherId, value) => {
    setAttendanceData((prev) =>
      prev.map((record) =>
        record.teacherId === teacherId ? { ...record, clockInTime: value || null } : record
      )
    )
  }

  const handleExitChange = (teacherId, value) => {
    setAttendanceData((prev) =>
      prev.map((record) =>
        record.teacherId === teacherId ? { ...record, clockOutTime: value || null } : record
      )
    )
  }

  const formatTimeFromDate = (dateVal) => {
    if (!dateVal) return null
    const d = new Date(dateVal)
    if (Number.isNaN(d.getTime())) return null
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
  }

  const handleSubmit = async () => {
    setLoading(true)
    setMessage({ type: "", content: "" })

    try {
      const token = localStorage.getItem("token")
      if (!token) {
        throw new Error("Authentication token not found")
      }

      const validAttendanceRecords = attendanceData.filter((record) => record.status !== "")

      if (validAttendanceRecords.length === 0) {
        throw new Error("Select attendance status for at least one teacher to save")
      }

      const loadingToastId = toast.loading("Marking attendance...")

      const results = await Promise.all(
        validAttendanceRecords.map(async (attendance) => {
          const formattedDate = new Date(selectedDate).toISOString().split("T")[0]

          const toClockTimePayload = (val) => {
            if (!val) return undefined
            if (typeof val === "string" && /^\d{1,2}:\d{2}$/.test(val)) return val
            const d = new Date(val)
            return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
          }
          const requestBody = {
            teacherId: attendance.teacherId,
            date: formattedDate,
            status: attendance.status,
            remarks: attendance.remarks || "",
            ...(toClockTimePayload(attendance.clockInTime) && { clockInTime: toClockTimePayload(attendance.clockInTime) }),
            ...(toClockTimePayload(attendance.clockOutTime) && { clockOutTime: toClockTimePayload(attendance.clockOutTime) }),
          }

          try {
            const response = await fetch(API_ENDPOINTS.teacherAttendanceMark, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(requestBody),
            })

            if (!response.ok) {
              const data = await response.json()
              throw new Error(data.message || `HTTP error! status: ${response.status}`)
            }

            const data = await response.json()
            return {
              success: true,
              teacherId: attendance.teacherId,
              status: attendance.status,
              remarks: attendance.remarks,
            }
          } catch (error) {
            return {
              success: false,
              teacherId: attendance.teacherId,
              error: error.message,
            }
          }
        }),
      )

      toast.dismiss(loadingToastId)

      const failures = results.filter((r) => !r.success)

      if (failures.length > 0) {
        setMessage({
          type: "error",
          content: `Failed to mark attendance for ${
            failures.length
          } teachers: ${failures.map((f) => f.error).join(", ")}`,
        })
        toast.error(`Failed to mark attendance for ${failures.length} teachers`)
      } else {
        const successfulSubmissions = results.filter((r) => r.success)
        setAttendanceData((prev) => {
          const updated = prev.map((record) => {
            const submission = successfulSubmissions.find((s) => s.teacherId === record.teacherId)
            if (submission) {
              return {
                ...record,
                status: submission.status,
                remarks: submission.remarks,
              }
            }
            return record
          })
          return updated
        })
        setMessage({
          type: "success",
          content: "Attendance marked successfully!",
        })
        toast.success("Attendance marked successfully!")
        fetchAttendance()
      }
    } catch (error) {
      console.error("Submit error:", error)
      setMessage({
        type: "error",
        content: "Error marking attendance: " + error.message,
      })
      toast.error("Error marking attendance: " + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleTeacherClick = (teacher) => {
    navigate(getTeacherProfileUrl(teacher))
  }

  const handleStaffAttendanceChange = (staffId, status) => {
    setStaffAttendanceData((prev) =>
      prev.map((record) => (record.staffId === staffId ? { ...record, status } : record)),
    )
    const s = staff.find((x) => x._id === staffId)
    const name = s ? getFullName(s) : "Staff"
    if (status === "present") toast.success(`${name} marked as present`)
    else if (status === "absent") toast.error(`${name} marked as absent`)
    else if (status === "half-day") toast.custom(<div className="bg-yellow-100 text-yellow-800 px-4 py-3 rounded-lg shadow">{name} marked as half-day</div>)
    else if (status === "leave") toast.custom(<div className="bg-blue-100 text-blue-800 px-4 py-3 rounded-lg shadow">{name} marked on leave</div>)
    else if (status === "unassigned") toast.custom(<div className="bg-gray-100 text-gray-700 px-4 py-3 rounded-lg shadow">{name} set to Unassigned</div>)
  }

  const handleStaffRemarkChange = (staffId, remarks) => {
    setStaffAttendanceData((prev) =>
      prev.map((record) => (record.staffId === staffId ? { ...record, remarks } : record)),
    )
  }

  const handleStaffEntryChange = (staffId, value) => {
    setStaffAttendanceData((prev) =>
      prev.map((record) => (record.staffId === staffId ? { ...record, clockInTime: value || null } : record)),
    )
  }

  const handleStaffExitChange = (staffId, value) => {
    setStaffAttendanceData((prev) =>
      prev.map((record) => (record.staffId === staffId ? { ...record, clockOutTime: value || null } : record)),
    )
  }

  const handleStaffSubmit = async () => {
    setLoading(true)
    setMessage({ type: "", content: "" })
    try {
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const validRecords = staffAttendanceData.filter((r) => r.status !== "")
      if (validRecords.length === 0) throw new Error("Select attendance status for at least one staff to save")
      const loadingToastId = toast.loading("Marking staff attendance...")
      const results = await Promise.all(
        validRecords.map(async (attendance) => {
          const formattedDate = new Date(staffSelectedDate).toISOString().split("T")[0]
          const toClockTimePayload = (val) => {
            if (!val) return undefined
            if (typeof val === "string" && /^\d{1,2}:\d{2}$/.test(val)) return val
            const d = new Date(val)
            return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
          }
          const requestBody = {
            staffId: attendance.staffId,
            type: "staff",
            date: formattedDate,
            status: attendance.status,
            remarks: attendance.remarks || "",
            ...(toClockTimePayload(attendance.clockInTime) && { clockInTime: toClockTimePayload(attendance.clockInTime) }),
            ...(toClockTimePayload(attendance.clockOutTime) && { clockOutTime: toClockTimePayload(attendance.clockOutTime) }),
          }
          try {
            const response = await fetch(API_ENDPOINTS.teacherAttendanceMark, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify(requestBody),
            })
            if (!response.ok) {
              const data = await response.json()
              throw new Error(data.message || `HTTP error! status: ${response.status}`)
            }
            return { success: true, staffId: attendance.staffId, status: attendance.status, remarks: attendance.remarks }
          } catch (error) {
            return { success: false, staffId: attendance.staffId, error: error.message }
          }
        }),
      )
      toast.dismiss(loadingToastId)
      const failures = results.filter((r) => !r.success)
      if (failures.length > 0) {
        setMessage({
          type: "error",
          content: `Failed to mark attendance for ${failures.length} staff: ${failures.map((f) => f.error).join(", ")}`,
        })
        toast.error(`Failed to mark attendance for ${failures.length} staff`)
      } else {
        setStaffAttendanceData((prev) =>
          prev.map((record) => {
            const submission = results.find((s) => s.staffId === record.staffId)
            if (submission?.success)
              return { ...record, status: submission.status, remarks: submission.remarks }
            return record
          }),
        )
        setMessage({ type: "success", content: "Staff attendance marked successfully!" })
        toast.success("Staff attendance marked successfully!")
        fetchStaffAttendance()
      }
    } catch (error) {
      setMessage({ type: "error", content: "Error marking staff attendance: " + error.message })
      toast.error("Error marking staff attendance: " + error.message)
    } finally {
      setLoading(false)
    }
  }

  const paginateStaffData = (data) => {
    const startIndex = (staffCurrentPage - 1) * itemsPerPage
    return data.slice(startIndex, startIndex + itemsPerPage)
  }

  const paginateData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return data.slice(startIndex, endIndex)
  }

  const sortTeacherData = (data, column, direction) => {
    return [...data].sort((a, b) => {
      let valueA, valueB

      if (column === "name") {
        valueA = getFullName(a).toLowerCase()
        valueB = getFullName(b).toLowerCase()
      } else if (column === "employeeCode") {
        valueA = (a.employeeCode || "").toLowerCase()
        valueB = (b.employeeCode || "").toLowerCase()
      }

      if (direction === "asc") {
        return valueA.localeCompare(valueB)
      } else {
        return valueB.localeCompare(valueA)
      }
    })
  }

  const handleTeacherSort = (column) => {
    const newDirection = sortColumn === column && sortDirection === "asc" ? "desc" : "asc"
    setSortColumn(column)
    setSortDirection(newDirection)

    const sortedTeachers = sortTeacherData(teachers, column, newDirection)
    setTeachers(sortedTeachers)
    setFilteredTeachers(sortedTeachers)
  }

  const getSortIcon = (column, currentSortColumn, currentSortDirection) => {
    if (column !== currentSortColumn) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />
    }

    return currentSortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />
    )
  }

  return (
    <div className="w-full mx-auto">
      <Tabs defaultValue="teacher" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6 bg-white p-1 rounded-lg">
          <TabsTrigger
            value="teacher"
            className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white"
          >
            Teacher Attendance
          </TabsTrigger>
          <TabsTrigger
            value="staff"
            className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white"
          >
            Staff Attendance
          </TabsTrigger>
          <TabsTrigger
            value="leave"
            className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white"
          >
            Leave Management
          </TabsTrigger>
        </TabsList>

        <TabsContent value="teacher">
          <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
            <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
              <CardTitle className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <span className="text-[#0263cc] text-xl font-semibold">Teacher Attendance Management</span>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="relative w-full sm:w-auto">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search teachers..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 pr-4 py-2 bg-white border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg w-full sm:w-64"
                    />
                  </div>

                  <div className="flex h-9 items-center gap-2 bg-white rounded-lg border border-[#0263cc]/20 px-3 py-2">
                    <Input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="border-0 p-0 focus:ring-0 w-full"
                    />
                  </div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {(message.content || fetchError) && (
                <Alert
                  variant={message.type === "error" ? "destructive" : "default"}
                  className={`m-4 rounded-lg ${
                    message.type === "success"
                      ? "bg-green-50 text-green-800 border-green-200"
                      : message.type === "error"
                        ? "bg-red-50 text-red-800 border-red-200"
                        : "bg-blue-50 text-blue-800 border-blue-200"
                  }`}
                >
                  <AlertDescription className="font-medium">{message.content || fetchError}</AlertDescription>
                </Alert>
              )}

              {loading && !attendanceData.length ? (
                <div className="flex justify-center items-center p-8">
                  <div className="flex items-center space-x-2">
                    <Clock className="animate-spin h-5 w-5 text-[#0263cc]" />
                    <span className="text-gray-600">Loading data...</span>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-y border-gray-200">
                        <th
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleTeacherSort("employeeCode")}
                        >
                          <div className="flex items-center">
                            Employee ID
                            {getSortIcon("employeeCode", sortColumn, sortDirection)}
                          </div>
                        </th>
                        <th
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={() => handleTeacherSort("name")}
                        >
                          <div className="flex items-center">
                            Name
                            {getSortIcon("name", sortColumn, sortDirection)}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Entry
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Exit
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Remarks
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredTeachers.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                            {searchQuery ? "No teachers found matching your search" : "No teachers available"}
                          </td>
                        </tr>
                      ) : (
                        paginateData(filteredTeachers).map((teacher) => {
                          const teacherAttendance = attendanceData.find((a) => a.teacherId === teacher._id) || {
                            status: "unassigned",
                            remarks: "",
                            clockInTime: null,
                            clockOutTime: null,
                          }
                          const entryInputValue = (() => {
                            if (teacherAttendance.clockInTime && typeof teacherAttendance.clockInTime === "string" && /^\d{1,2}:\d{2}$/.test(teacherAttendance.clockInTime)) return teacherAttendance.clockInTime
                            const t = formatTimeFromDate(teacherAttendance.clockInTime)
                            return t ? t.slice(0, 5) : ""
                          })()
                          const exitInputValue = (() => {
                            if (teacherAttendance.clockOutTime && typeof teacherAttendance.clockOutTime === "string" && /^\d{1,2}:\d{2}$/.test(teacherAttendance.clockOutTime)) return teacherAttendance.clockOutTime
                            const t = formatTimeFromDate(teacherAttendance.clockOutTime)
                            return t ? t.slice(0, 5) : ""
                          })()

                          return (
                            <tr key={teacher._id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-4 text-sm text-gray-600">{teacher.employeeCode || "N/A"}</td>
                              <td className="px-4 py-4">
                                <div className="flex items-center space-x-3">
                                  {teacher.profileImage ? (
                                    <img
                                      src={teacher.profileImage || "/placeholder.svg"}
                                      alt={getFullName(teacher)}
                                      className="h-8 w-8 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="h-8 w-8 rounded-full bg-[#0263cc]/10 flex items-center justify-center">
                                      <span className="text-[#0263cc] text-sm font-medium">{getInitial(teacher)}</span>
                                    </div>
                                  )}
                                  <span
                                    className="text-[#0263cc] hover:text-[#0263cc]/80 cursor-pointer font-medium"
                                    onClick={() => handleTeacherClick(teacher)}
                                  >
                                    {getFullName(teacher)}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 align-top">
                                <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-3 min-w-[200px]">
                                  <RadioGroup
                                    value={attendanceData.find((a) => a.teacherId === teacher._id)?.status === "not-marked" ? "unassigned" : (attendanceData.find((a) => a.teacherId === teacher._id)?.status || "unassigned")}
                                    onValueChange={(value) => handleAttendanceChange(teacher._id, value)}
                                    className="grid grid-cols-2 gap-x-6 gap-y-2.5 [&_[data-state=checked]+circle]:fill-[#8B73EF] [&_.border-primary]:border-[#8B73EF]"
                                  >
                                    {[
                                      { value: "unassigned", label: "Unassigned" },
                                      { value: "present", label: "Present" },
                                      { value: "absent", label: "Absent" },
                                      { value: "half-day", label: "Half Day" },
                                      { value: "leave", label: "Leave" },
                                    ].map((opt) => (
                                      <label
                                        key={opt.value}
                                        className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 whitespace-nowrap"
                                      >
                                        <RadioGroupItem
                                          value={opt.value}
                                          className="shrink-0 border-[#8B73EF] text-[#8B73EF]"
                                        />
                                        <span>{opt.label}</span>
                                      </label>
                                    ))}
                                  </RadioGroup>
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <Input
                                  type="time"
                                  value={entryInputValue}
                                  onChange={(e) => handleEntryChange(teacher._id, e.target.value)}
                                  className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg max-w-[120px]"
                                />
                              </td>
                              <td className="px-4 py-4">
                                <Input
                                  type="time"
                                  value={exitInputValue}
                                  onChange={(e) => handleExitChange(teacher._id, e.target.value)}
                                  className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg max-w-[120px]"
                                />
                              </td>
                              <td className="px-4 py-4">
                                <Input
                                  type="text"
                                  placeholder="Add remarks"
                                  value={teacherAttendance.remarks || ""}
                                  onChange={(e) => handleRemarkChange(teacher._id, e.target.value)}
                                  className="w-full border-gray-300 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg"
                                />
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <Pagination
                currentPage={currentPage}
                totalItems={filteredTeachers.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
                itemLabel="teachers"
              />
              <div className="flex justify-end p-4 border-t border-gray-200">
                <Button
                  onClick={handleSubmit}
                  disabled={loading || attendanceData.length === 0}
                  className="bg-[#0263cc] hover:bg-[#0263cc]/90 text-white px-6 py-2 rounded-lg disabled:bg-[#0263cc]/50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 shadow-sm"
                >
                  {loading && <Clock className="animate-spin h-4 w-4" />}
                  <span>Mark Attendance</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="staff">
          <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
            <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
              <CardTitle className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <span className="text-[#0263cc] text-xl font-semibold">Staff Attendance Management</span>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="relative w-full sm:w-auto">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search staff..."
                      value={staffSearchQuery}
                      onChange={(e) => setStaffSearchQuery(e.target.value)}
                      className="pl-9 pr-4 py-2 bg-white border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg w-full sm:w-64"
                    />
                  </div>
                  <div className="flex h-9 items-center gap-2 bg-white rounded-lg border border-[#0263cc]/20 px-3 py-2">
                    <Input
                      type="date"
                      value={staffSelectedDate}
                      onChange={(e) => setStaffSelectedDate(e.target.value)}
                      className="border-0 p-0 focus:ring-0 w-full"
                    />
                  </div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {(message.content || fetchError) && (
                <Alert
                  variant={message.type === "error" ? "destructive" : "default"}
                  className={`m-4 rounded-lg ${
                    message.type === "success"
                      ? "bg-green-50 text-green-800 border-green-200"
                      : message.type === "error"
                        ? "bg-red-50 text-red-800 border-red-200"
                        : "bg-blue-50 text-blue-800 border-blue-200"
                  }`}
                >
                  <AlertDescription className="font-medium">{message.content || fetchError}</AlertDescription>
                </Alert>
              )}
              {loading && !staffAttendanceData.length ? (
                <div className="flex justify-center items-center p-8">
                  <div className="flex justify-center items-center space-x-2">
                    <Clock className="animate-spin h-5 w-5 text-[#0263cc]" />
                    <span className="text-gray-600">Loading staff...</span>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-y border-gray-200">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Employee ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Entry
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Exit
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Remarks
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredStaff.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                            {staffSearchQuery ? "No staff found matching your search" : "No staff available"}
                          </td>
                        </tr>
                      ) : (
                        paginateStaffData(filteredStaff).map((s) => {
                          const rec = staffAttendanceData.find((a) => a.staffId === s._id) || {
                            status: "unassigned",
                            remarks: "",
                            clockInTime: null,
                            clockOutTime: null,
                          }
                          const entryVal =
                            rec.clockInTime && typeof rec.clockInTime === "string" && /^\d{1,2}:\d{2}$/.test(rec.clockInTime)
                              ? rec.clockInTime
                              : formatTimeFromDate(rec.clockInTime)
                                ? formatTimeFromDate(rec.clockInTime).slice(0, 5)
                                : ""
                          const exitVal =
                            rec.clockOutTime && typeof rec.clockOutTime === "string" && /^\d{1,2}:\d{2}$/.test(rec.clockOutTime)
                              ? rec.clockOutTime
                              : formatTimeFromDate(rec.clockOutTime)
                                ? formatTimeFromDate(rec.clockOutTime).slice(0, 5)
                                : ""
                          return (
                            <tr key={s._id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-4 text-sm text-gray-600">{s.staffId || s.employeeCode || "N/A"}</td>
                              <td className="px-4 py-4">
                                <div className="flex items-center space-x-3">
                                  {s.profileImage ? (
                                    <img
                                      src={s.profileImage || "/placeholder.svg"}
                                      alt={getFullName(s)}
                                      className="h-8 w-8 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="h-8 w-8 rounded-full bg-[#0263cc]/10 flex items-center justify-center">
                                      <span className="text-[#0263cc] text-sm font-medium">{getInitial(s)}</span>
                                    </div>
                                  )}
                                  <span className="font-medium text-gray-900">{getFullName(s)}</span>
                                </div>
                              </td>
                              <td className="px-4 py-4 align-top">
                                <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-3 min-w-[200px]">
                                  <RadioGroup
                                    value={rec.status}
                                    onValueChange={(v) => handleStaffAttendanceChange(s._id, v)}
                                    className="flex flex-wrap gap-2"
                                  >
                                    <div className="flex items-center space-x-2">
                                      <RadioGroupItem value="present" id={`staff-present-${s._id}`} />
                                      <label htmlFor={`staff-present-${s._id}`} className="text-sm cursor-pointer">Present</label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <RadioGroupItem value="absent" id={`staff-absent-${s._id}`} />
                                      <label htmlFor={`staff-absent-${s._id}`} className="text-sm cursor-pointer">Absent</label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <RadioGroupItem value="half-day" id={`staff-half-${s._id}`} />
                                      <label htmlFor={`staff-half-${s._id}`} className="text-sm cursor-pointer">Half-day</label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <RadioGroupItem value="leave" id={`staff-leave-${s._id}`} />
                                      <label htmlFor={`staff-leave-${s._id}`} className="text-sm cursor-pointer">Leave</label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <RadioGroupItem value="unassigned" id={`staff-unassigned-${s._id}`} />
                                      <label htmlFor={`staff-unassigned-${s._id}`} className="text-sm cursor-pointer">Unassigned</label>
                                    </div>
                                  </RadioGroup>
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <Input
                                  type="time"
                                  value={entryVal}
                                  onChange={(e) => handleStaffEntryChange(s._id, e.target.value)}
                                  className="w-28"
                                />
                              </td>
                              <td className="px-4 py-4">
                                <Input
                                  type="time"
                                  value={exitVal}
                                  onChange={(e) => handleStaffExitChange(s._id, e.target.value)}
                                  className="w-28"
                                />
                              </td>
                              <td className="px-4 py-4">
                                <Input
                                  placeholder="Remarks"
                                  value={rec.remarks}
                                  onChange={(e) => handleStaffRemarkChange(s._id, e.target.value)}
                                  className="max-w-[180px]"
                                />
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              <Pagination
                currentPage={staffCurrentPage}
                totalItems={filteredStaff.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setStaffCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
                itemLabel="staff"
              />
              <div className="flex justify-end p-4 border-t border-gray-200">
                <Button
                  onClick={handleStaffSubmit}
                  disabled={loading || staffAttendanceData.length === 0}
                  className="bg-[#0263cc] hover:bg-[#0263cc]/90 text-white px-6 py-2 rounded-lg disabled:bg-[#0263cc]/50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 shadow-sm"
                >
                  {loading && <Clock className="animate-spin h-4 w-4" />}
                  <span>Mark Attendance</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leave">
          <LeaveManagement />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default TeacherAttendance
